import React from 'react';
import styled from 'styled-components';
import { cloneDeep } from 'lodash-es';
import { shallowEqual } from 'react-redux';
//import { useNavigate, useMatches } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '@/store';
import {
  fetchRankSystems,
  fetchRankDetails,
  fetchLoyaltyWallets,
  fetchShopCampaigns,
  claimRankTitlePrize,
} from '@/store/slices/rankSystems';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import fetchGamesInfo from '@/utils/games-info-api-cache';

import './index.scss';

type RankSystemsProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const requested = {
  rankDetails: false,
  rankSystems: false,
  loyaltyWallets: false,
  shopCampaigns: false,
};

const RankSystems = (componentProps: RankSystemsProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();

  const { rankGroups, shopCampaigns, wallets, loading, loaded, authentication, ranks, currencies } = useAppSelector(
    (state) => ({
      authentication: state.authentication,
      rankGroups: state.rankSystems.rankGroups,
      shopCampaigns: state.rankSystems.shopCampaigns,
      wallets: state.rankSystems.wallets,
      loading: state.rankSystems.loading,
      loaded: state.rankSystems.loaded,
      ranks: state.rankSystems.ranks,
      currencies: state.rankSystems.loyaltyWallets?.currencies ?? [],
    }),
    shallowEqual,
  );

  const gameIds: any = React.useMemo(() => {
    const gameIdsHash: any = {};

    ranks?.forEach?.((rank) => {
      rank.rank_campaigns?.forEach?.((rankCampaign: any) => {
        if (Array.isArray(rankCampaign.cms_data?.ui_front?.slots)) {
          rankCampaign.cms_data.ui_front.slots.forEach((gameId: number) => {
            gameIdsHash[gameId] = true;
          });
        }
      });
    });

    shopCampaigns?.sc?.forEach?.((sc: any) => {
      if (Array.isArray(sc.cms_data?.ui_front?.slots)) {
        sc.cms_data.ui_front.slots.forEach((gameId: number) => {
          gameIdsHash[gameId] = true;
        });
      }
    });

    return Object.keys(gameIdsHash);
  }, [ranks, shopCampaigns]);

  const {
    data: { data: allCampaignGames },
  } = fetchGamesInfo({
    dsId: window.config.dataSourceAllGames,
    gameIds: gameIds,
    authenticationToken: authentication?.access_token,
  });

  React.useEffect(() => {
    if (!requested.rankDetails) {
      dispatch(fetchRankDetails());
      requested.rankDetails = true;
    }

    if (!requested.rankSystems) {
      dispatch(fetchRankSystems());
      requested.rankSystems = true;
    }
    if (!requested.loyaltyWallets) {
      dispatch(fetchLoyaltyWallets());
      requested.loyaltyWallets = true;
    }
    if (!requested.shopCampaigns) {
      dispatch(fetchShopCampaigns());
      requested.shopCampaigns = true;
    }

    return () => {
      requested.rankDetails = false;
      requested.rankSystems = false;
      requested.loyaltyWallets = false;
      requested.shopCampaigns = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onClaimPrize = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const prizeKey = event.currentTarget.dataset.prizeKey;
      if (prizeKey) {
        let rankTitle: any = null;
        ranks?.forEach?.((rank) => {
          if (rankTitle) return; // Exit early if rankTitle is found
          rank.rank_titles?.forEach?.((title: any) => {
            if (rankTitle) return; // Exit early if rankTitle is found

            if (title?.cms_data?.ui_front?.rank_prize?.prize_key === prizeKey) {
              rankTitle = title;
            }
          });
        });

        if (rankTitle) {
          dispatch(claimRankTitlePrize({ prizeKey }))
            .then((response) => {
              if (response?.payload?.success) {
                console.log('Prize claimed successfully:', response.payload);
              } else {
                console.error('Failed to claim prize:', response.payload);
              }
            })
            .catch((error) => {
              console.error('Error claiming prize:', error);
            });
        }
      }
    },
    [ranks, dispatch],
  );

  const contextValue = React.useMemo(() => {
    const gamesData: any = {};
    (allCampaignGames ?? []).forEach((game: any) => {
      gamesData[game.id] = game;
    });

    const localRanks = cloneDeep(ranks ?? []);

    localRanks.forEach((rankGroup) => {
      rankGroup.rank_campaigns?.forEach?.((rankCampaign: any) => {
        let games: any[] = [];
        let recommendedGames: any[] = [];
        let sportFilters: any[] = [];

        if (Array.isArray(rankCampaign.cms_data?.ui_front?.slots) && rankCampaign.cms_data.ui_front.slots.length > 0) {
          games = rankCampaign.cms_data.ui_front.slots;
        }
        if (
          Array.isArray(rankCampaign.cms_data?.ui_front?.recommended) &&
          rankCampaign.cms_data.ui_front.recommended.length > 0
        ) {
          recommendedGames = rankCampaign.cms_data?.ui_front?.recommended;
        }
        if (
          Array.isArray(rankCampaign.cms_data?.ui_front?.filters?.filter_prematch) &&
          rankCampaign.cms_data.ui_front.filters.filter_prematch.length > 0
        ) {
          sportFilters = rankCampaign.cms_data.ui_front.filters.filter_prematch;
        }

        if (rankCampaign?.cms_data?.ui_front) {
          rankCampaign.cms_data.ui_front.games = games
            .map((gameId: number) => {
              return gamesData[gameId] || null;
            })
            .filter((game: any) => game !== null);
          rankCampaign.cms_data.ui_front.recommendedGames = recommendedGames
            .map((gameId: number) => {
              return gamesData[gameId] || null;
            })
            .filter((game: any) => game !== null);
          rankCampaign.cms_data.ui_front.sportFilters = sportFilters
            .filter((t) => typeof t.idBet === 'undefined')
            .slice(0, 20)
            .map((filter, i) => {
              if (!filter) return null;
              let id = '';

              if (typeof filter.idSport != 'undefined') id = filter.idSport;
              if (typeof filter.idCategory != 'undefined') id += '/' + filter.idCategory;
              if (typeof filter.idTournament != 'undefined') id += '/' + filter.idTournament;

              return {
                name: filter.name,
                url: `/bets/pre-match/${id}`,
              };
            });
        }
      });
    });

    let defLoyaltyCard = null;
    if (!rankGroups || (Array.isArray(rankGroups) && rankGroups.length === 0)) {
      if (localRanks && Array.isArray(localRanks) && localRanks.length > 0) {
        const lr = localRanks[0] ?? null;
        console.log('No rank groups found, using first rank as default group', { localRanks, lr });

        if (lr && lr.rank_titles && Array.isArray(lr.rank_titles) && lr.rank_titles.length > 0) {
          const cr = lr.rank_titles[0] ?? null;
          const nr = lr.rank_titles[1] ?? null;

          defLoyaltyCard = {
            current_rank: {
              name: cr?.cms_data?.ui_front?.name ?? '',
              logo: cr?.cms_data?.ui_front?.logo ?? '',
            },
            next_rank: {
              name: nr?.cms_data?.ui_front?.name ?? '',
              logo: nr?.cms_data?.ui_front?.logo ?? '',
            },
          };
        }
      }
    }

    const localShopCampaigns = cloneDeep(shopCampaigns ?? []);
    localShopCampaigns?.sc?.forEach?.((sc: any) => {
      let games: any[] = [];
      let recommendedGames: any[] = [];
      let sportFilters: any[] = [];

      if (Array.isArray(sc.cms_data?.ui_front?.slots) && sc.cms_data.ui_front.slots.length > 0) {
        games = sc.cms_data.ui_front.slots;
      }
      if (Array.isArray(sc.cms_data?.ui_front?.recommended) && sc.cms_data.ui_front.recommended.length > 0) {
        recommendedGames = sc.cms_data?.ui_front?.recommended;
      }
      if (
        Array.isArray(sc.cms_data?.ui_front?.filters?.filter_prematch) &&
        sc.cms_data.ui_front.filters.filter_prematch.length > 0
      ) {
        sportFilters = sc.cms_data.ui_front.filters.filter_prematch;
      }

      if (sc?.cms_data?.ui_front) {
        sc.cms_data.ui_front.games = games
          .map((gameId: number) => {
            return gamesData[gameId] || null;
          })
          .filter((game: any) => game !== null);
        sc.cms_data.ui_front.recommendedGames = recommendedGames
          .map((gameId: number) => {
            return gamesData[gameId] || null;
          })
          .filter((game: any) => game !== null);
        sc.cms_data.ui_front.sportFilters = sportFilters
          .filter((t) => typeof t.idBet === 'undefined')
          .slice(0, 20)
          .map((filter, i) => {
            if (!filter) return null;
            let id = '';

            if (typeof filter.idSport != 'undefined') id = filter.idSport;
            if (typeof filter.idCategory != 'undefined') id += '/' + filter.idCategory;
            if (typeof filter.idTournament != 'undefined') id += '/' + filter.idTournament;

            return {
              name: filter.name,
              url: `/bets/pre-match/${id}`,
            };
          });
      }
    });

    //console.log('default group', defLoyaltyCard);
    return {
      actions: {
        onClaimPrize,
      },
      rank_groups: rankGroups,
      wallets: wallets,
      loading: loading,
      loaded: loaded,
      currencies: currencies,
      ranks: localRanks,
      def_rank: defLoyaltyCard,
      shopCampaigns: localShopCampaigns,
    };
  }, [onClaimPrize, rankGroups, wallets, loading, loaded, currencies, ranks, allCampaignGames, shopCampaigns]);

  if (!rankGroups || (Array.isArray(rankGroups) && rankGroups.length === 0)) {
    if (!contextValue.def_rank) {
      return null;
    }
  }

  if (
    authentication &&
    (['user', 'token'].indexOf(authentication.auth_type) === -1 || authentication.access_token === null)
  ) {
    return null;
  }

  console.log('RankSystems[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default RankSystems;
